import type { RegionConnection } from '@crewdle/auth-bridge';

export interface OrgRegionResolverDeps {
  /** `AuthBridge.connectRegion` for the org. */
  connect: (orgId: string) => Promise<RegionConnection>;
  /** True when this build configures at least one remote (non-ca) region. */
  remoteConfigured: () => boolean;
  /** The default (global = ca) app's connection. */
  homeConnection: () => RegionConnection;
  warn?: (message: string, error: unknown) => void;
}

/**
 * Data residency: resolves an org's region connection for the org context.
 *
 * - ca results are memoized per org (the region is immutable), so loading the
 *   user's orgs and binding the active one costs one directory read per org.
 * - A FAILED directory read (offline, transient, rules not yet deployed) while
 *   this build configures NO remote region (prod today: every org is ca) is
 *   treated as ca with a warning instead of "region unavailable". With a remote
 *   region configured (staging) it still fails closed — an org of that region
 *   must never fall back to the ca project. Fallback results are not memoized.
 * - auth-bridge's own errors (no session, no config for the directory's
 *   region, region-token refused) always propagate: a directory entry saying
 *   'us' with no us config stays unavailable.
 */
export class OrgRegionResolver {
  private readonly home = new Map<string, RegionConnection>();

  constructor(private readonly deps: OrgRegionResolverDeps) {}

  async resolve(orgId: string): Promise<RegionConnection> {
    const cached = this.home.get(orgId);
    if (cached) return cached;
    try {
      const connection = await this.deps.connect(orgId);
      if (!connection.isRegional) this.home.set(orgId, connection);
      return connection;
    } catch (error) {
      if (!this.deps.remoteConfigured() && isDirectoryReadError(error)) {
        (this.deps.warn ?? console.warn)(`[OrgContext] orgDirectory read failed for ${orgId}; no remote region configured → ca`, error);
        return this.deps.homeConnection();
      }
      throw error;
    }
  }

  clear(): void {
    this.home.clear();
  }
}

/** A Firestore failure of the directory read (not an auth-bridge decision). */
export function isDirectoryReadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  if (error.message.startsWith('auth-bridge:')) return false;
  return typeof (error as { code?: unknown }).code === 'string';
}
